import { z } from "zod";
import { TAX_AI_MODEL, TAX_PRODUCT_IMAGE_MODEL, aiEstimateCostFields, refineAiEstimateCost, } from "./ai-model-policy.js";
import { applianceProductDiscoveryResultSchema, applianceProductDiscoveryTargetSchema, } from "./appliance-product-discovery.js";
import { browserAgentFindingCreateSchema, browserAgentRunStatusSchema, browserAgentTriggerSchema, } from "./browser-agent.js";
import { ufSchema } from "./calculation.js";
import { environmentSchema } from "./context.js";
import { evidenceLocatorSchema } from "./evidence-locator.js";
import { productImageGeneratedIllustrationSchema, productImageResultSchema, productImageTargetSchema, } from "./product-image-enrichment.js";
import { stAgreementKeySchema, stAgreementObservationSchema } from "./st-agreements.js";
/**
 * Web research sweeps (plan "web fiscal research sweep", rollout PRs 2–3).
 *
 * An admin plans a sweep over NCMs × routes. The API prefilters NCMs through
 * the Convênio ICMS 142/18 CEST catalog and plans research units at the level
 * where the law is written (segment × destination UF), plus the UF internal
 * rate and interstate table units. A runtime worker (`WEB_RESEARCH_AGENT`)
 * claims units with a lease, locates official documents with grounded search,
 * extracts annex rows from our own fetched copy, and reports results.
 *
 * Nothing here creates, approves, publishes or calculates a rule: annex
 * entries and web findings are review material.
 */
export const WEB_RESEARCH_LEASE_MINUTES = 15;
/**
 * A worker still working on a unit (a long extraction) extends its lease with
 * `POST /internal/v1/web-research/units/{unitId}/lease`, at most until this
 * long after the claim, so a stuck worker cannot hold a unit forever.
 */
export const WEB_RESEARCH_MAX_LEASE_MINUTES = 60;
export const WEB_RESEARCH_MAX_UNIT_ATTEMPTS = 3;
/** A RUNNING web run whose heartbeat is younger than this blocks a new run. */
export const WEB_RESEARCH_RUN_STALE_SECONDS = 120;
export const WEB_RESEARCH_MAX_PAIR_UNITS = 2_000;
/**
 * Units the runner works on at once (asyncio slots in the one Cloud Run task).
 * The per-minute paces, the daily grounded cap and the monthly budget stay
 * global: the API admits claims against them under one advisory lock, and the
 * runner's shared token buckets space the provider calls themselves.
 */
export const WEB_RESEARCH_MAX_WORKER_CONCURRENCY = 32;
export const WEB_RESEARCH_DEFAULT_WORKER_CONCURRENCY = 8;
/** Largest `GET /internal/v1/web-research/work?limit=` (one claim per free slot). */
export const WEB_RESEARCH_MAX_WORK_LIMIT = WEB_RESEARCH_MAX_WORKER_CONCURRENCY;
/** Window of the live throughput shown on a sweep (units settled per minute). */
export const WEB_RESEARCH_THROUGHPUT_WINDOW_MINUTES = 10;
/**
 * A unit BLOCKED on a document FAILS as FETCH_TIMEOUT when the document is
 * still not ingested this long after its download was requested (a failed
 * download or read fails it at once as FETCH_FAILED).
 */
export const WEB_RESEARCH_FETCH_INGEST_TIMEOUT_MINUTES = 120;
/** Backstop: a unit BLOCKED this long FAILS as FETCH_TIMEOUT whatever the fetch state. */
export const WEB_RESEARCH_BLOCKED_TIMEOUT_HOURS = 6;
/** Characters per page of `GET /internal/v1/web-research/documents/{id}/text`. */
export const WEB_RESEARCH_TEXT_PAGE_CHARS = 60_000;
const ncmSchema = z.string().regex(/^\d{8}$/);
const cestSchema = z.string().regex(/^\d{2}\.\d{3}\.\d{2}$/);
const segmentSchema = z.string().regex(/^\d{2}$/);
const rateSchema = z.string().regex(/^(0|[1-9]\d{0,5})(\.\d{1,8})?$/);
/** US dollars as a decimal string, at most 6 decimals (micro-dollar precision). */
export const usdAmountSchema = z.string().regex(/^(0|[1-9]\d{0,7})(\.\d{1,6})?$/);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
/* -- Scope ----------------------------------------------------------------- */
export const researchRouteSchema = z
    .object({ originUf: ufSchema, destinationUf: ufSchema })
    .strict();
/** Which origin → destination routes a sweep covers for each NCM. */
export const researchRouteSelectorSchema = z.discriminatedUnion("mode", [
    /** Only routes seen in calculations for that NCM (within the scope's window). */
    z
        .object({ mode: z.literal("USED") })
        .strict(),
    z
        .object({
        mode: z.literal("EXPLICIT"),
        routes: z.array(researchRouteSchema).min(1).max(729),
    })
        .strict(),
    /** Every route from and/or to one UF (26 or 52 routes). */
    z
        .object({
        mode: z.literal("UF_ALL"),
        uf: ufSchema,
        direction: z.enum(["FROM", "TO", "BOTH"]),
    })
        .strict(),
    /** Every destination UF, origin irrelevant. Only with `strategy: "SEGMENT"`. */
    z
        .object({ mode: z.literal("ALL_DESTINATIONS") })
        .strict(),
]);
/** Closed research targets, not instructions or proof of applicability. No
 * names, identifiers, prices, invoices or page text may enter this context. */
export const webResearchContextSchema = z
    .object({
    calculationDate: z.string().date(),
    ncm: ncmSchema,
    cest: cestSchema.nullable(),
    originUf: ufSchema,
    destinationUf: ufSchema,
    transaction: z
        .object({
        kind: z.enum(["PURCHASE", "SALE"]),
        supplierType: z.enum(["INDUSTRY", "DISTRIBUTOR", "UNKNOWN"]),
        recipientIcmsTaxpayer: z.boolean().nullable(),
        goodsOrigin: z
            .string()
            .regex(/^[0-8]$/)
            .nullable(),
        priorSt: z.enum(["PAID", "RETAINED", "UNKNOWN"]),
        federalRegime: z.enum(["CUMULATIVE", "NON_CUMULATIVE", "UNKNOWN"]),
        productQualification: z.literal("UNACCREDITED"),
    })
        .strict()
        .optional(),
})
    .strict();
export const webResearchContextsSchema = z
    .array(webResearchContextSchema)
    .min(1)
    .max(20)
    .superRefine((contexts, ctx) => {
    const cohort = new Set(contexts.map((entry) => JSON.stringify([entry.calculationDate, entry.transaction ?? null])));
    if (cohort.size > 1)
        ctx.addIssue({
            code: "custom",
            message: "Research contexts must share one target date and transaction profile",
        });
});
export const researchSweepScopeSchema = z.discriminatedUnion("kind", [
    /** NCMs (and CEST/routes) BackChannel calculated in the last `days`. */
    z
        .object({
        kind: z.literal("USED"),
        researchContexts: webResearchContextsSchema.optional(),
        days: z.number().int().min(1).max(730).default(365),
        routes: researchRouteSelectorSchema.default({ mode: "USED" }),
    })
        .strict(),
    /** Active NCMs of the official catalog, optionally limited to chapters. */
    z
        .object({
        kind: z.literal("CATALOG"),
        researchContexts: webResearchContextsSchema.optional(),
        chapters: z
            .array(z.string().regex(/^\d{2}$/))
            .max(97)
            .default([]),
        routes: researchRouteSelectorSchema.default({ mode: "ALL_DESTINATIONS" }),
    })
        .strict(),
    /** An explicit list pasted or uploaded by an operator. */
    z
        .object({
        kind: z.literal("LIST"),
        researchContexts: webResearchContextsSchema.optional(),
        /** Automatic calculator research can discover registered legal documents without Search. */
        discoveryMode: z.literal("REVIEWED_SOURCES").optional(),
        ncms: z.array(ncmSchema).min(1).max(10_000),
        routes: researchRouteSelectorSchema.default({ mode: "ALL_DESTINATIONS" }),
    })
        .strict(),
    /**
     * CONFAZ ST agreements (signatory matrix): reads the official page of each
     * agreement of the reviewed seed (`ST_AGREEMENT_CATALOG`, optionally
     * limited to `agreementKeys` or `segments`) and, with `locate`, one
     * grounded CONFAZ locate per segment to find other agreements and their
     * amending acts. Strategy SEGMENT only; no NCM, no route.
     */
    z
        .object({
        kind: z.literal("CONFAZ_AGREEMENTS"),
        researchContexts: webResearchContextsSchema.optional(),
        agreementKeys: z.array(stAgreementKeySchema).max(100).default([]),
        segments: z
            .array(z.string().regex(/^\d{2}$/))
            .max(40)
            .default([]),
        locate: z.boolean().default(true),
        routes: researchRouteSelectorSchema.default({ mode: "ALL_DESTINATIONS" }),
    })
        .strict(),
]);
/** Read-only compatibility: public write plans keep researchSweepScopeSchema. */
export const researchSweepStoredScopeSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("APPLIANCE_PRODUCTS"),
        targetIds: z.array(z.string().uuid()).min(1).max(5000),
        uploadId: z.string().uuid(),
        sourceTaskId: z.string().uuid(),
        targetManifestSha256: z.string().regex(/^[a-f0-9]{64}$/),
    })
        .strict(),
    z
        .object({
        kind: z.literal("PRODUCT_IMAGES"),
        researchContexts: z.never().optional(),
        catalogId: z.string().uuid(),
        revisionId: z.string().uuid(),
        jobId: z.string().uuid(),
    })
        .strict(),
    researchSweepScopeSchema.options[0].extend({
        researchContexts: webResearchContextsSchema.optional(),
    }),
    researchSweepScopeSchema.options[1].extend({
        researchContexts: webResearchContextsSchema.optional(),
    }),
    researchSweepScopeSchema.options[2].extend({
        researchContexts: webResearchContextsSchema.optional(),
    }),
    researchSweepScopeSchema.options[3].extend({
        researchContexts: webResearchContextsSchema.optional(),
    }),
]);
export const researchSweepStrategySchema = z.enum(["SEGMENT", "PAIR"]);
const sweepPlanFields = {
    scope: researchSweepScopeSchema,
    strategy: researchSweepStrategySchema.default("SEGMENT"),
    /** Hard cap on grounded (search) calls; the sweep stops at BUDGET_EXHAUSTED. */
    maxGroundedCalls: z.number().int().min(1).max(100_000),
    /** Hard cap on estimated spend in US dollars. */
    budgetUsd: usdAmountSchema,
    /** Locate units answered within this many days (any sweep) are CACHED. */
    skipFresh: z
        .object({ days: z.number().int().min(1).max(365) })
        .strict()
        .nullable()
        .default({ days: 30 }),
    /** Queue IOB tasks for the N most-used pairs (USED scope only). */
    iobCertifyTopN: z.number().int().min(0).max(3_000).default(0),
    /** Upper bound on VERIFY_PAIR units for `strategy: "PAIR"`. */
    maxPairUnits: z.number().int().min(1).max(WEB_RESEARCH_MAX_PAIR_UNITS).default(2_000),
};
function refineSweepPlan(value, context) {
    if (value.scope.researchContexts?.length) {
        const scope = value.scope;
        if (scope.kind !== "LIST" || scope.routes.mode !== "EXPLICIT") {
            context.addIssue({
                code: "custom",
                path: ["scope", "researchContexts"],
                message: "Typed applicability research requires LIST and EXPLICIT routes",
            });
        }
        else {
            const routes = scope.routes.routes;
            if (scope.researchContexts?.some((entry) => !scope.ncms.includes(entry.ncm) ||
                !routes.some((route) => route.originUf === entry.originUf && route.destinationUf === entry.destinationUf)))
                context.addIssue({
                    code: "custom",
                    path: ["scope", "researchContexts"],
                    message: "Every research context must belong to the requested NCM and route scope",
                });
        }
    }
    if (value.strategy === "PAIR") {
        if (value.scope.kind === "CATALOG" || value.scope.kind === "CONFAZ_AGREEMENTS") {
            context.addIssue({
                code: "custom",
                path: ["strategy"],
                message: "PAIR is only allowed for LIST or USED scopes",
            });
        }
        if (value.scope.routes.mode === "ALL_DESTINATIONS") {
            context.addIssue({
                code: "custom",
                path: ["scope", "routes", "mode"],
                message: "ALL_DESTINATIONS is only allowed with strategy SEGMENT",
            });
        }
    }
    if (value.iobCertifyTopN > 0 && value.scope.kind !== "USED") {
        context.addIssue({
            code: "custom",
            path: ["iobCertifyTopN"],
            message: "IOB certification of the most-used pairs needs a USED scope",
        });
    }
}
export const researchSweepCreateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    name: z.string().trim().min(1).max(120),
    /** Explicit attachment for task-funded manual batches; never inferred from a monthly cap. */
    budgetCampaignId: z.string().uuid().optional(),
    ...sweepPlanFields,
})
    .strict()
    .superRefine(refineSweepPlan);
/** Same body as create; `name` is optional. Read-only: nothing is stored. */
export const researchSweepPreviewRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    name: z.string().trim().min(1).max(120).optional(),
    budgetCampaignId: z.string().uuid().optional(),
    ...sweepPlanFields,
})
    .strict()
    .superRefine(refineSweepPlan);
/* -- Units ----------------------------------------------------------------- */
export const researchUnitKindSchema = z.enum([
    "LOCATE_APPLIANCE_PRODUCT",
    "LOCATE_PRODUCT_IMAGE",
    "LOCATE_SEGMENT_UF",
    "LOCATE_CONFAZ_SEGMENT",
    "LOCATE_INTERNAL_RATE_UF",
    "LOCATE_INTERSTATE_TABLE",
    "FETCH_DOCUMENT",
    "EXTRACT_DOCUMENT_SEGMENT",
    "VERIFY_PAIR",
]);
/** Kinds that spend a grounded (Google Search) call when claimed. */
export const GROUNDED_RESEARCH_UNIT_KINDS = [
    "LOCATE_APPLIANCE_PRODUCT",
    "LOCATE_PRODUCT_IMAGE",
    "LOCATE_SEGMENT_UF",
    "LOCATE_CONFAZ_SEGMENT",
    "LOCATE_INTERNAL_RATE_UF",
    "LOCATE_INTERSTATE_TABLE",
    "VERIFY_PAIR",
];
/** Kinds that spend a non-grounded structured extraction call. */
export const EXTRACTION_RESEARCH_UNIT_KINDS = [
    "EXTRACT_DOCUMENT_SEGMENT",
];
export const researchUnitStatusSchema = z.enum([
    "PENDING",
    /** Waits for its document to be ingested. */
    "BLOCKED",
    "IN_PROGRESS",
    "DONE",
    /** Answered recently by another sweep (`skipFresh`). */
    "CACHED",
    /**
     * Gave up after MAX_UNIT_ATTEMPTS or a permanent error: the document it
     * waited for could not be downloaded or read (FETCH_FAILED, FETCH_TIMEOUT),
     * or an operator closed the sweep (CLOSED_BY_OPERATOR). Retry with
     * RETRY_FAILED.
     */
    "FAILED",
    /** Sweep cancelled before the unit ran. */
    "SKIPPED",
]);
/** What the unit researches. Fields present depend on the kind. */
export const researchUnitKeySchema = z
    .object({
    applianceProductTarget: applianceProductDiscoveryTargetSchema.optional(),
    appliancePbeChecked: z.literal(true).optional(),
    applianceTargetSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .optional(),
    applianceDiscoveryCheckpoint: applianceProductDiscoveryResultSchema.optional(),
    applianceProductDocuments: z
        .array(z
        .object({
        evidenceId: z.string().uuid(),
        manufacturerIdentityId: z.string().uuid(),
        contentSha256: z.string().regex(/^[a-f0-9]{64}$/),
        normalizedSha256: z.string().regex(/^[a-f0-9]{64}$/),
    })
        .strict())
        .max(5)
        .optional(),
    /** Deterministic official-source discovery. Never dispatches Google Search. */
    discoveryMode: z.literal("REVIEWED_SOURCES").optional(),
    imageTarget: productImageTargetSchema.optional(),
    discoveryCheckpoint: productImageResultSchema.optional(),
    generatedIllustrationCheckpoint: productImageGeneratedIllustrationSchema.optional(),
    researchContexts: webResearchContextsSchema.optional(),
    segment: segmentSchema.optional(),
    segmentName: z.string().min(1).max(300).optional(),
    /**
     * LOCATE_CONFAZ_SEGMENT: official CONFAZ pages of the catalog agreements
     * for this segment. The worker cites them only when grounding accepts no
     * citation. Absent on units planned before the field; claim fills them
     * from the catalog. Other kinds omit it.
     */
    reviewedEntryUrls: z.array(z.string().url().max(2_000)).max(20).optional(),
    /** CESTs of the segment the sweep's NCMs matched (LOCATE_SEGMENT_UF / EXTRACT). */
    cests: z.array(cestSchema).max(500).optional(),
    uf: ufSchema.optional(),
    destinationUf: ufSchema.optional(),
    /** VERIFY_PAIR groups every claimed origin for one NCM × destination. */
    originUfs: z.array(ufSchema).max(27).optional(),
    ncm: ncmSchema.optional(),
    cest: cestSchema.nullable().optional(),
    canonicalUrl: z.string().url().max(2_000).optional(),
    /** Reviewed source the URL was allowlisted by. */
    sourceId: z.string().uuid().optional(),
    sourceKey: z.string().min(1).max(120).nullable().optional(),
    evidenceId: z.string().uuid().optional(),
    contentSha256: sha256Schema.optional(),
    /** EXTRACT_DOCUMENT_SEGMENT: text page cursor (one unit per page). */
    cursor: z.string().min(1).max(500).optional(),
    /**
     * EXTRACT_DOCUMENT_SEGMENT: what the page is read for. Absent = SEGMENT
     * (annex rows of `cests`); INTERNAL_RATE (the `uf` modal rate) and
     * INTERSTATE_TABLE (Senate interstate rates) come from rate locates.
     */
    extractMode: z
        .enum(["SEGMENT", "INTERNAL_RATE", "INTERSTATE_TABLE", "CONFAZ_AGREEMENT"])
        .optional(),
    /** CONFAZ_AGREEMENT: the agreement the page is read for (from the seed or the URL). */
    agreementKey: stAgreementKeySchema.optional(),
    /** VERIFY_PAIR phase 2: documents located in phase 1, waiting to be ingested. */
    documentUrls: z.array(z.string().url().max(2_000)).max(10).optional(),
    /** VERIFY_PAIR phase 2: the ingested documents the worker reads. */
    documents: z
        .array(z
        .object({
        evidenceId: z.string().uuid(),
        url: z.string().url().max(2_000),
        sourceKey: z.string().min(1).max(120).nullable(),
    })
        .strict())
        .max(10)
        .optional(),
})
    .strict();
export const researchUnitErrorSchema = z
    .object({
    code: z.string().regex(/^[A-Z][A-Z0-9_]{1,79}$/),
    message: z.string().max(1_000),
    retryable: z.boolean(),
})
    .strict();
/** Provider usage reported by the worker; the API prices it with the settings' priceTable. */
export const researchUnitUsageSchema = z
    .object({
    groundedCalls: z.number().int().min(0).max(20).default(0),
    /** Non-grounded structured extraction calls (chunks, escalations). */
    extractionCalls: z.number().int().min(0).max(200).default(0),
    /** Fixed-price Vertex Imagen outputs; no text-token usage is inferred. */
    generatedImages: z.number().int().min(0).max(20).optional(),
    promptTokens: z.number().int().min(0).max(10_000_000).default(0),
    outputTokens: z.number().int().min(0).max(1_000_000).default(0),
    /** Provider-reported Google queries, not grounded prompts. Omitted means unknown. */
    searchQueries: z.number().int().min(0).max(100_000).optional(),
    /** Per-model billed tokens, including thinking in outputTokens. */
    modelUsage: z
        .array(z
        .object({
        model: z.string().min(1).max(120),
        promptTokens: z.number().int().min(0).max(10_000_000),
        outputTokens: z.number().int().min(0).max(1_000_000),
    })
        .strict())
        .max(20)
        .optional(),
    models: z.array(z.string().min(1).max(80)).max(20).default([]),
})
    .strict();
export const researchUnitStoredUsageSchema = researchUnitUsageSchema.extend({
    /** Provider-reported Google queries, not grounded prompts. Omitted means unknown. */
    searchQueries: z.number().int().min(0).max(100_000).optional(),
    /** Per-model billed tokens, including thinking in outputTokens. */
    modelUsage: z
        .array(z
        .object({
        model: z.string().min(1).max(120),
        promptTokens: z.number().int().min(0).max(10_000_000),
        outputTokens: z.number().int().min(0).max(1_000_000),
    })
        .strict())
        .max(20)
        .optional(),
});
export const researchUnitSchema = z
    .object({
    id: z.string().uuid(),
    sweepId: z.string().uuid(),
    kind: researchUnitKindSchema,
    key: researchUnitKeySchema,
    status: researchUnitStatusSchema,
    priority: z.number().int(),
    attempts: z.number().int().nonnegative(),
    notBefore: z.string().datetime(),
    leasedUntil: z.string().datetime().nullable(),
    lastError: researchUnitErrorSchema.nullable(),
    result: z.record(z.string(), z.unknown()).nullable(),
    usage: researchUnitStoredUsageSchema.nullable(),
    costUsd: usdAmountSchema,
    updatedAt: z.string().datetime(),
})
    .strict();
const pageOf = (item) => z
    .object({
    items: z.array(item).max(200),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
    totalEstimate: z.number().int().nonnegative(),
})
    .strict();
export const researchUnitPageSchema = pageOf(researchUnitSchema);
/* -- Preview and sweeps ----------------------------------------------------- */
const unitCountsFields = Object.fromEntries(researchUnitKindSchema.options
    .filter((kind) => kind !== "LOCATE_PRODUCT_IMAGE" && kind !== "LOCATE_APPLIANCE_PRODUCT")
    .map((kind) => [kind, z.number().int().nonnegative()]));
export const researchUnitKindCountsSchema = z
    .object(unitCountsFields)
    .extend({
    LOCATE_PRODUCT_IMAGE: z.number().int().nonnegative().optional(),
    LOCATE_APPLIANCE_PRODUCT: z.number().int().nonnegative().optional(),
})
    .strict();
export const researchSweepEstimateSchema = z
    .object({
    /** Grounded calls including a retry and alternate-query allowance. */
    groundedCalls: z.number().int().nonnegative(),
    extractionCalls: z.number().int().nonnegative(),
    ...aiEstimateCostFields,
    /** Historical projection only; never used for paid-call admission or as a bill. */
    usageForecast: z
        .object({
        costUsd: usdAmountSchema,
        basis: z.literal("SETTLED_PLATFORM_COST"),
        groundedSamples: z.number().int().nonnegative(),
        extractionSamples: z.number().int().nonnegative(),
        unresolvedCalls: z.number().int().nonnegative(),
        windowDays: z.number().int().min(1).max(365),
    })
        .strict()
        .optional(),
    etaMinutes: z.number().int().nonnegative(),
    /** Planned grounded calls fit maxGroundedCalls and the estimate fits budgetUsd. */
    withinCaps: z.boolean(),
})
    .strict()
    .superRefine((value, ctx) => {
    refineAiEstimateCost(value, ctx);
    if (value.costUsd === null && value.withinCaps)
        ctx.addIssue({
            code: "custom",
            path: ["withinCaps"],
            message: "Unavailable cost cannot fit a budget",
        });
});
export const researchSweepPreviewSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    ncms: z
        .object({
        total: z.number().int().nonnegative(),
        /** Matched a CEST of Conv. 142/18 (candidate for ST). */
        withCest: z.number().int().nonnegative(),
        /** No CEST prefix matched: outside Conv. 142/18 (no research unit). */
        withoutCest: z.number().int().nonnegative(),
        /** Matched more than one CEST at the longest prefix. */
        ambiguousCest: z.number().int().nonnegative(),
    })
        .strict(),
    routes: z.number().int().nonnegative(),
    destinationUfs: z.array(ufSchema).max(27),
    segments: z
        .array(z
        .object({
        segment: segmentSchema,
        segmentName: z.string().max(300),
        cests: z.number().int().nonnegative(),
        ncms: z.number().int().nonnegative(),
    })
        .strict())
        .max(100),
    units: researchUnitKindCountsSchema,
    /** Locate units already answered within skipFresh (created as CACHED). */
    cachedUnits: z.number().int().nonnegative(),
    estimate: researchSweepEstimateSchema,
    warnings: z.array(z.string().min(1).max(300)).max(20),
    /** sha256 of the normalized plan request; the create response echoes it. */
    planDigest: sha256Schema,
})
    .strict();
export const researchSweepStatusSchema = z.enum([
    "QUEUED",
    "RUNNING",
    "PAUSED",
    "COMPLETED",
    "CANCELLED",
    "BUDGET_EXHAUSTED",
]);
/**
 * How a COMPLETED sweep ended. COMPLETED_WITH_FAILURES: at least one unit
 * FAILED (reasons group them by error code). OPERATOR: closed with the CLOSE
 * action; AUTOMATIC: every unit reached a terminal state on its own.
 */
export const researchSweepCompletionSchema = z
    .object({
    outcome: z.enum(["COMPLETED", "COMPLETED_WITH_FAILURES"]),
    closedBy: z.enum(["AUTOMATIC", "OPERATOR"]),
    failedUnits: z.number().int().nonnegative(),
    skippedUnits: z.number().int().nonnegative(),
    reasons: z
        .array(z
        .object({
        code: z.string().regex(/^[A-Z][A-Z0-9_]{1,79}$/),
        count: z.number().int().positive(),
    })
        .strict())
        .max(20),
})
    .strict();
export const researchSweepTotalsSchema = z
    .object({
    units: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
    inProgress: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
    cached: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    byKind: researchUnitKindCountsSchema,
})
    .strict();
/**
 * Live pace of a sweep over the last WEB_RESEARCH_THROUGHPUT_WINDOW_MINUTES:
 * units that reached a terminal state (and grounded calls reported) per
 * minute, and the ETA of its open units at that real rate (null while nothing
 * settled in the window). Only the sweep detail carries it.
 */
export const researchSweepThroughputSchema = z
    .object({
    windowMinutes: z.number().int().positive(),
    unitsSettled: z.number().int().nonnegative(),
    unitsPerMinute: z.number().nonnegative(),
    groundedCallsPerMinute: z.number().nonnegative(),
    openUnits: z.number().int().nonnegative(),
    inProgress: z.number().int().nonnegative(),
    etaSeconds: z.number().int().nonnegative().nullable(),
    measuredAt: z.string().datetime(),
})
    .strict();
export const researchSweepSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.string().uuid(),
    name: z.string().min(1).max(120),
    scope: researchSweepStoredScopeSchema,
    strategy: researchSweepStrategySchema,
    status: researchSweepStatusSchema,
    maxGroundedCalls: z.number().int().positive(),
    budgetUsd: usdAmountSchema,
    spentUsd: usdAmountSchema,
    groundedCalls: z.number().int().nonnegative(),
    extractionCalls: z.number().int().nonnegative(),
    skipFresh: z
        .object({ days: z.number().int().min(1).max(365) })
        .strict()
        .nullable(),
    iobCertifyTopN: z.number().int().nonnegative(),
    iobTaskIds: z.array(z.string().uuid()).max(729),
    totals: researchSweepTotalsSchema,
    /** Set when the sweep is COMPLETED; null otherwise. */
    completion: researchSweepCompletionSchema.nullable(),
    /** Sweep detail only (absent on list pages). */
    throughput: researchSweepThroughputSchema.nullable().optional(),
    estimate: researchSweepEstimateSchema,
    planDigest: sha256Schema,
    lookupJobId: z.string().uuid().nullable(),
    createdBy: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    startedAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
    revision: z.number().int().positive(),
})
    .strict();
export const researchSweepPageSchema = pageOf(researchSweepSchema);
export const researchSweepActionSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    /**
     * RETRY_STALLED re-requests the downloads blocked units wait for (live
     * sweeps). CLOSE finishes a live sweep now: waiting units FAIL as
     * CLOSED_BY_OPERATOR (RETRY_FAILED reopens them) and the sweep COMPLETES.
     */
    action: z.enum(["PAUSE", "RESUME", "CANCEL", "RETRY_FAILED", "RETRY_STALLED", "CLOSE"]),
})
    .strict();
/* -- Settings (admin-editable) ---------------------------------------------- */
export const webResearchPriceTableSchema = z
    .object({
    /** Per 1,000 grounded prompts (Gemini 2.5 family list price). */
    groundedPromptUsdPer1000: usdAmountSchema,
    /** Per 1,000 search queries (Gemini 3 family bills per query); "0" when unused. */
    searchQueryUsdPer1000: usdAmountSchema,
    inputTokensUsdPerMillion: usdAmountSchema,
    outputTokensUsdPerMillion: usdAmountSchema,
    /** Explicit per-model prices. Legacy mixed usage is conservatively priced at its highest rate. */
    modelTokenPrices: z
        .record(z.string().min(1).max(120), z
        .object({
        inputTokensUsdPerMillion: usdAmountSchema,
        outputTokensUsdPerMillion: usdAmountSchema,
    })
        .strict())
        .optional(),
})
    .strict();
export const webResearchStoredPriceTableSchema = webResearchPriceTableSchema.extend({
    /** Explicit per-model prices. Legacy mixed usage is conservatively priced at its highest rate. */
    modelTokenPrices: z
        .record(z.string().min(1).max(120), z
        .object({
        inputTokensUsdPerMillion: usdAmountSchema,
        outputTokensUsdPerMillion: usdAmountSchema,
    })
        .strict())
        .optional(),
});
const webResearchSettingsFields = {
    /** Kill switch: the next claim returns no work. */
    paused: z.boolean(),
    groundedCallsPerMinute: z.number().int().min(1).max(30),
    extractionCallsPerMinute: z.number().int().min(1).max(60),
    dailyGroundedCap: z.number().int().min(1).max(100_000),
    monthlyBudgetUsd: usdAmountSchema,
    priceTable: webResearchPriceTableSchema,
};
/** Units the runner works on at once (see WEB_RESEARCH_MAX_WORKER_CONCURRENCY). */
const workerConcurrencySchema = z.number().int().min(1).max(WEB_RESEARCH_MAX_WORKER_CONCURRENCY);
export const webResearchSettingsUpdateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    ...webResearchSettingsFields,
    /** Omitted: the current value is kept. */
    workerConcurrency: workerConcurrencySchema.optional(),
})
    .strict();
export const webResearchSettingsSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    ...webResearchSettingsFields,
    priceTable: webResearchStoredPriceTableSchema,
    workerConcurrency: workerConcurrencySchema,
    updatedAt: z.string().datetime().nullable(),
    updatedBy: z.string().uuid().nullable(),
    revision: z.number().int().nonnegative(),
})
    .strict();
export const WEB_RESEARCH_DEFAULT_SETTINGS = {
    schemaVersion: "1.0.0",
    paused: false,
    groundedCallsPerMinute: 20,
    extractionCallsPerMinute: 40,
    workerConcurrency: WEB_RESEARCH_DEFAULT_WORKER_CONCURRENCY,
    dailyGroundedCap: 1_500,
    monthlyBudgetUsd: "300",
    priceTable: {
        // Argon bills search queries, whose pre-call bound remains unavailable.
        groundedPromptUsdPer1000: "0",
        searchQueryUsdPer1000: "14",
        inputTokensUsdPerMillion: "4",
        outputTokensUsdPerMillion: "20",
        modelTokenPrices: {
            [TAX_AI_MODEL]: { inputTokensUsdPerMillion: "4", outputTokensUsdPerMillion: "20" },
        },
    },
};
/* -- Annex entries (EXTRACT_DOCUMENT_SEGMENT output) ------------------------ */
/** One ST row read from an official annex, with the verbatim quote it came from. */
export const annexEntrySchema = z
    .object({
    cest: cestSchema.nullable(),
    ncmPrefixes: z.array(z.string().regex(/^\d{2,8}$/)).max(50),
    description: z.string().max(2_000).nullable(),
    stScope: z.enum(["INTERNAL_ONLY", "ORIGINS", "ALL_ORIGINS"]),
    originUfs: z.array(ufSchema).max(27),
    originalMva: rateSchema.nullable(),
    adjustedMva: z
        .object({
        r04: rateSchema.nullable(),
        r07: rateSchema.nullable(),
        r12: rateSchema.nullable(),
    })
        .strict()
        .nullable(),
    internalRate: rateSchema.nullable(),
    validFrom: z.string().date().nullable(),
    validTo: z.string().date().nullable(),
    legalBasis: z.string().max(1_000).nullable(),
    /** Where `rowQuote` sits in the normalized text (character offsets). */
    locator: z
        .object({
        textStart: z.number().int().nonnegative(),
        textEnd: z.number().int().nonnegative(),
    })
        .strict()
        .nullable(),
    rowQuote: z.string().min(1).max(4_000),
    checks: z
        .object({
        passed: z.boolean(),
        failures: z.array(z.string().min(1).max(120)).max(20),
    })
        .strict(),
})
    .strict();
/* -- Runtime (worker → API), Google ID token with WEB_RESEARCH_AGENT -------- */
export const webResearchRunStartSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    runId: z.string().uuid(),
    environment: environmentSchema,
    trigger: browserAgentTriggerSchema,
    agentVersion: z.string().min(1).max(80),
    startedAt: z.string().datetime(),
})
    .strict();
export const webResearchRunSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.string().uuid(),
    environment: environmentSchema,
    source: z.literal("WEB_RESEARCH"),
    trigger: browserAgentTriggerSchema,
    status: browserAgentRunStatusSchema,
    agentVersion: z.string().min(1).max(80),
    startedAt: z.string().datetime(),
    heartbeatAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
    unitsDone: z.number().int().nonnegative(),
    findingsCount: z.number().int().nonnegative(),
    errorsCount: z.number().int().nonnegative(),
    lastError: z.string().max(1_000).nullable(),
    stopRequested: z.boolean(),
    revision: z.number().int().positive(),
})
    .strict();
/** Heartbeat / progress / finish. */
export const webResearchRunUpdateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    runId: z.string().uuid(),
    status: browserAgentRunStatusSchema,
    unitsDone: z.number().int().nonnegative(),
    unitsFailed: z.number().int().nonnegative().default(0),
    unitsReleased: z.number().int().nonnegative().default(0),
    lastError: z.string().max(1_000).nullable(),
    currentMessage: z.string().max(500).nullable().optional(),
    at: z.string().datetime(),
})
    .strict();
export const webResearchRunControlSchema = z
    .object({
    stopRequested: z.boolean(),
    paused: z.boolean(),
    status: browserAgentRunStatusSchema,
})
    .strict();
/**
 * A leased unit. `key` is the worker view of the unit (JSON object), by kind:
 * - LOCATE_*: `{segment?, segmentName?, destinationUf?, uf?, sourceIds,
 *   reviewedEntryUrls?}` (`reviewedEntryUrls`: CONFAZ catalog pages);
 * - EXTRACT_DOCUMENT_SEGMENT: `{evidenceId, segment, segmentName?,
 *   destinationUf?, cursor?, cests: [{cest, ncmPrefixes, description}]}`, or
 *   for rate documents `{evidenceId, extractMode, uf?, destinationUf?, cursor?}`;
 * - VERIFY_PAIR: `{ncm, cest?, destinationUf, originUfs, segment?,
 *   segmentName?, cests, sourceIds, documents?: [{evidenceId, url, sourceKey}]}`
 *   (no `documents` = locate phase; with `documents` = verify phase).
 * FETCH_DOCUMENT units are dispatched by the API and never handed out.
 */
export const webResearchWorkUnitSchema = z
    .object({
    id: z.string().uuid(),
    sweepId: z.string().uuid(),
    kind: researchUnitKindSchema,
    key: z.record(z.string(), z.unknown()),
    /** Reserved grounded calls for this lease; older APIs omit it (worker defaults to 1). */
    groundedCallBudget: z.union([z.literal(1), z.literal(2)]).optional(),
    /** Every provider invocation must obtain its own durable monetary reservation. */
    paidCallRequired: z.literal(true).optional(),
    budgetCampaignId: z.string().uuid().optional(),
    /** Attempts including this lease (1-based). */
    attempts: z.number().int().min(1).max(WEB_RESEARCH_MAX_UNIT_ATTEMPTS),
    /** Send as `If-Match` on the result; a stale token answers 409 LEASE_LOST. */
    leaseToken: z.string().uuid(),
    leasedUntil: z.string().datetime(),
})
    .strict();
export const webResearchPaidCallReserveSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    callId: z.string().uuid(),
    leaseToken: z.string().uuid(),
    model: z.enum([TAX_AI_MODEL, TAX_PRODUCT_IMAGE_MODEL]),
    grounded: z.boolean(),
    /** Required by the server for the product-discovery unit; legacy fiscal envelopes omit it. */
    purpose: z.literal("PRODUCT_CLASSIFICATION").optional(),
    maxOutputTokens: z.number().int().min(1).max(32_768),
    maxOutputImages: z.literal(1).optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.purpose && (!value.grounded || value.model !== TAX_AI_MODEL))
        context.addIssue({
            code: "custom",
            path: ["purpose"],
            message: "Product classification requires the grounded leased model",
        });
    if (value.model === TAX_PRODUCT_IMAGE_MODEL) {
        if (value.grounded || value.maxOutputImages !== 1 || value.maxOutputTokens !== 1) {
            context.addIssue({
                code: "custom",
                path: ["maxOutputImages"],
                message: "Imagen reservations are tool-less and bound to exactly one output image",
            });
        }
    }
    else if (value.maxOutputImages !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["maxOutputImages"],
            message: "Image output bounds are only valid for the Imagen model",
        });
    }
});
export const webResearchPaidCallReservationSchema = z
    .object({
    callId: z.string().uuid(),
    reservedUsd: usdAmountSchema,
    dispatchAllowed: z.boolean(),
    replayed: z.boolean(),
})
    .strict();
export const webResearchPaidCallSettleSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    leaseToken: z.string().uuid(),
    usage: z
        .object({
        promptTokens: z.number().int().min(0).max(1_048_576),
        /** Includes billed thinking tokens. */
        outputTokens: z.number().int().min(0).max(32_768),
        searchQueries: z.number().int().min(0).max(100_000).optional(),
        imageCount: z.literal(1).optional(),
    })
        .strict(),
    outcome: z.enum(["SUCCEEDED", "FAILED"]),
})
    .strict();
export const webResearchPaidCallSettlementSchema = z
    .object({
    callId: z.string().uuid(),
    settledUsd: usdAmountSchema,
    replayed: z.boolean(),
})
    .strict();
export const webResearchStopReasonSchema = z.enum([
    "PAUSED",
    "MONTHLY_BUDGET",
    "DAILY_CAP",
    "RATE_LIMITED",
    "NO_WORK",
    "BUDGET_CAMPAIGN_REQUIRED",
    "CAMPAIGN_BUDGET",
    "STRICT_COST_BOUNDS_UNAVAILABLE",
]);
export const webResearchBudgetSchema = z
    .object({
    groundedCallsThisMinute: z.number().int().nonnegative(),
    extractionCallsThisMinute: z.number().int().nonnegative(),
    /** Brasília calendar day. */
    dailyGroundedUsed: z.number().int().nonnegative(),
    dailyGroundedRemaining: z.number().int().nonnegative(),
    monthlySpentUsd: usdAmountSchema,
    /** Unreported expired/ambiguous grounded holds, estimated rather than reported spend. */
    monthlyUnreportedReservationUsd: usdAmountSchema.optional(),
    monthlyRemainingUsd: usdAmountSchema,
    /** Grounded calls the worker may still spend now (minute, day and sweep caps). */
    groundedCallsLeft: z.number().int().nonnegative(),
    /** Micro-dollars left before the monthly budget or the sweeps' budgets stop claims. */
    usdMicrosLeft: z.number().int().nonnegative(),
    sweeps: z
        .array(z
        .object({
        sweepId: z.string().uuid(),
        groundedCallsRemaining: z.number().int().nonnegative(),
        budgetRemainingUsd: usdAmountSchema,
    })
        .strict())
        .max(50),
})
    .strict();
export const webResearchWorkSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    units: z.array(webResearchWorkUnitSchema).max(50),
    settings: webResearchSettingsSchema,
    budget: webResearchBudgetSchema,
    /**
     * Sweeps still QUEUED or RUNNING with open units (pending, blocked or in
     * progress). With no units handed out, a positive count tells the runner
     * that work is coming (downloads, back-offs, other slots' results) and it
     * should poll again instead of exiting.
     */
    activeSweeps: z.number().int().nonnegative(),
    /** Units of this organization leased right now (every runner slot). */
    inFlight: z.number().int().nonnegative(),
    /** Why no (more) units were handed out; null when the limit was filled. */
    stopReason: webResearchStopReasonSchema.nullable(),
})
    .strict();
/** A LOCATE citation (allowlisted by the worker against the unit's reviewed sources). */
export const webResearchCitationSchema = z
    .object({
    url: z.string().url().max(2_000),
    /** URL after one-hop redirect resolution; defaults to `url`. */
    canonicalUrl: z.string().url().max(2_000).optional(),
    host: z.string().max(253).optional(),
    sourceId: z.string().uuid().optional(),
    sourceKey: z.string().min(1).max(120).nullable().optional(),
    title: z.string().max(500).nullable().optional(),
    /** GROUNDED: a Google Search citation; REVIEWED_ENTRY: a reviewed registry entry URL. */
    origin: z.enum(["GROUNDED", "REVIEWED_ENTRY"]).optional(),
})
    .passthrough();
/**
 * Locate yield, for operators: counts and hosts of rejected grounded
 * citations. Never page text or model prose.
 */
export const webResearchLocateDiagnosticsSchema = z.object({
    candidateCount: z.number().int().min(0).max(1_000),
    acceptedCount: z.number().int().min(0).max(1_000),
    reviewedEntryCount: z.number().int().min(0).max(20).default(0),
    rejected: z
        .partialRecord(z.enum(["HOST_NOT_ALLOWED", "PATH_NOT_ALLOWED", "REDIRECT_UNRESOLVED", "INVALID_URL"]), z.number().int().min(0).max(1_000))
        .default({}),
    rejectedHosts: z
        .array(z.object({
        host: z.string().max(253),
        reason: z.enum(["HOST_NOT_ALLOWED", "PATH_NOT_ALLOWED", "REDIRECT_UNRESOLVED"]),
        count: z.number().int().min(1).max(1_000),
        official: z.boolean(),
    }))
        .max(20)
        .default([]),
});
/** LOCATE_* result, and VERIFY_PAIR phase 1 (`phase: "LOCATED"`). */
export const webResearchLocateResultSchema = z
    .object({
    phase: z.literal("LOCATED").optional(),
    citations: z.array(webResearchCitationSchema).max(20),
    searchQueries: z.array(z.string().max(1_000)).max(50).optional(),
    // Diagnostics never make a locate result invalid: malformed ones are dropped.
    diagnostics: webResearchLocateDiagnosticsSchema.optional().catch(undefined),
})
    .passthrough();
/** One annex row as the worker read it: values as displayed ("71,78", "01/08/2026"). */
export const webResearchExtractedRowSchema = z
    .object({
    cest: cestSchema,
    ncmCited: z.array(z.string().max(20)).max(50).default([]),
    description: z.string().max(2_000).default(""),
    stScope: z.enum(["INTERNAL_ONLY", "ORIGINS", "ALL_ORIGINS"]),
    originUfs: z.array(ufSchema).max(27).default([]),
    originalMva: z.string().max(40).nullable().default(null),
    adjustedMva: z
        .object({
        r04: z.string().max(40).nullable().optional(),
        r07: z.string().max(40).nullable().optional(),
        r12: z.string().max(40).nullable().optional(),
    })
        .nullable()
        .default(null),
    internalRate: z.string().max(40).nullable().default(null),
    validFrom: z.string().max(40).nullable().default(null),
    validTo: z.string().max(40).nullable().default(null),
    legalBasis: z.string().max(1_000).nullable().default(null),
    rowQuote: z.string().min(1).max(4_000),
    locator: z
        .object({
        textStart: z.number().int().nonnegative(),
        textEnd: z.number().int().nonnegative(),
    })
        .nullable()
        .default(null),
    checks: z.object({
        passed: z.boolean(),
        failures: z.array(z.string().min(1).max(120)).max(20),
    }),
})
    .passthrough();
/**
 * A rate read from an official text (extractMode INTERNAL_RATE or
 * INTERSTATE_TABLE), with the verbatim quote it came from. `rate` is as
 * displayed ("18%", "doze por cento"); `rateValue` is the same number as a
 * plain percentage ("18", "12"), read by the worker from a fixed table.
 * Interstate rules carry their origin and destination UFs, expanded by the
 * worker from the regions and states the quote names (fixed IBGE table).
 */
export const webResearchExtractedRateSchema = z
    .object({
    kind: z.enum(["INTERNAL_RATE", "INTERSTATE_RATE"]),
    /** INTERNAL_RATE: GENERAL (modal) or SPECIFIC; INTERSTATE_RATE: GENERAL, REGIONAL, IMPORTED. */
    scope: z.enum(["GENERAL", "SPECIFIC", "REGIONAL", "IMPORTED"]),
    rate: z.string().min(1).max(60),
    rateValue: z.string().max(40).nullable(),
    fcpRate: z.string().max(60).nullable().default(null),
    fcpRateValue: z.string().max(40).nullable().default(null),
    originUfs: z.array(ufSchema).max(27).default([]),
    destinationUfs: z.array(ufSchema).max(27).default([]),
    validFrom: z.string().max(40).nullable().default(null),
    validTo: z.string().max(40).nullable().default(null),
    legalBasis: z.string().max(1_000).nullable().default(null),
    rowQuote: z.string().min(1).max(4_000),
    locator: z
        .object({
        textStart: z.number().int().nonnegative(),
        textEnd: z.number().int().nonnegative(),
    })
        .nullable()
        .default(null),
    checks: z.object({
        passed: z.boolean(),
        failures: z.array(z.string().min(1).max(120)).max(20),
    }),
})
    .passthrough();
/** EXTRACT_DOCUMENT_SEGMENT result for one text page. */
export const webResearchExtractResultSchema = z
    .object({
    evidenceId: z.string().uuid().optional(),
    contentSha256: sha256Schema.optional(),
    nextCursor: z.string().min(1).max(500).nullable().optional(),
    entries: z.array(webResearchExtractedRowSchema).max(1_000),
    /** Rate modes only. */
    rates: z.array(webResearchExtractedRateSchema).max(100).optional(),
    /** CONFAZ_AGREEMENT only: the agreement read from the whole document. */
    agreements: z.array(stAgreementObservationSchema).max(5).optional(),
})
    .passthrough();
/** VERIFY_PAIR phase 2: findings were posted to POST /runs/{runId}/findings first. */
export const webResearchVerifiedResultSchema = z
    .object({
    phase: z.literal("VERIFIED"),
    findingIds: z.array(z.string().uuid()).max(27),
})
    .passthrough();
/**
 * `PUT /internal/v1/web-research/units/{unitId}/result`, `If-Match: <leaseToken>`.
 * `result` is interpreted by the unit's kind (schemas above).
 */
export const webResearchUnitResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    status: z.enum(["DONE", "FAILED"]),
    result: z.record(z.string(), z.unknown()).nullable(),
    usage: researchUnitUsageSchema,
    /** Required when FAILED. retryable=true re-queues until MAX_UNIT_ATTEMPTS. */
    error: researchUnitErrorSchema.nullable().default(null),
    /** Fenced pre-dispatch proof for a failed unit; never refunds paid-call holds. */
    groundedCallBudgetUnused: z.literal(true).optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.groundedCallBudgetUnused &&
        (value.status !== "FAILED" ||
            value.usage.groundedCalls !== 0 ||
            value.usage.extractionCalls !== 0 ||
            value.usage.promptTokens !== 0 ||
            value.usage.outputTokens !== 0 ||
            (value.usage.searchQueries ?? 0) !== 0)) {
        context.addIssue({
            code: "custom",
            path: ["groundedCallBudgetUnused"],
            message: "Unused attestation requires a failed unit with zero provider usage",
        });
    }
    if (value.status === "FAILED" && !value.error) {
        context.addIssue({ code: "custom", path: ["error"], message: "Required when FAILED" });
    }
    if (value.status === "DONE" && !value.result) {
        context.addIssue({ code: "custom", path: ["result"], message: "Required when DONE" });
    }
});
/**
 * Gives the lease back without consuming the attempt (429/503/SIGTERM). The
 * lease token is optional (body or If-Match); when sent it must be current.
 */
export const webResearchUnitReleaseSchema = z
    .object({
    schemaVersion: z.literal("1.0.0").optional(),
    reason: z.enum(["RATE_LIMITED", "SHUTDOWN", "PROVIDER_UNAVAILABLE"]),
    /** Runtime attests that no grounded request was dispatched under this lease. */
    groundedCallBudgetUnused: z.literal(true).optional(),
    leaseToken: z.string().uuid().optional(),
    /** Provider Retry-After, when known; otherwise exponential back-off applies. */
    retryAfterSeconds: z.number().int().min(0).max(3_600).nullable().optional(),
})
    .strict();
/**
 * `POST /internal/v1/web-research/units/{unitId}/lease`: the worker is still
 * working on the unit under this lease. The lease moves to
 * now + WEB_RESEARCH_LEASE_MINUTES, never past claim + WEB_RESEARCH_MAX_LEASE_MINUTES.
 * Paces, reservations and budgets are unchanged: the unit stays in flight.
 * A stale or expired lease answers 409 LEASE_LOST.
 */
export const webResearchUnitLeaseExtensionSchema = z
    .object({
    schemaVersion: z.literal("1.0.0").optional(),
    leaseToken: z.string().uuid(),
})
    .strict();
export const webResearchUnitLeaseSchema = z
    .object({
    unitId: z.string().uuid(),
    leasedUntil: z.string().datetime(),
    /** False when the lease already sits at its WEB_RESEARCH_MAX_LEASE_MINUTES cap. */
    extended: z.boolean(),
})
    .strict();
export const webResearchUnitReceiptSchema = z
    .object({
    unitId: z.string().uuid(),
    status: researchUnitStatusSchema,
    attempts: z.number().int().nonnegative(),
    notBefore: z.string().datetime(),
    /** Units the API planned from this result (FETCH/EXTRACT). */
    createdUnits: z.number().int().nonnegative(),
})
    .strict();
/**
 * A VERIFY_PAIR finding: the browser agent finding shape with source
 * WEB_RESEARCH, no screenshot and the unit it answers.
 */
export const webResearchFindingCreateSchema = browserAgentFindingCreateSchema
    .extend({
    source: z.literal("WEB_RESEARCH"),
    frame: z.null().default(null),
    researchUnitId: z.string().uuid(),
})
    .strict();
/** One page of an ingested document's normalized text. */
export const webResearchDocumentTextSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    evidenceId: z.string().uuid(),
    contentSha256: sha256Schema,
    /** Canonical URL of the document. */
    url: z.string().url().max(2_000),
    /** Character offset (Unicode code points) of `text` in the whole document. */
    offset: z.number().int().nonnegative(),
    textEnd: z.number().int().nonnegative(),
    totalChars: z.number().int().nonnegative(),
    text: z.string(),
    /** Opaque; pass as `cursor` for the next page. Null on the last page. */
    nextCursor: z.string().min(1).nullable(),
    locators: z.array(evidenceLocatorSchema).max(2_000),
})
    .strict();
//# sourceMappingURL=web-research.js.map